import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queriesApi } from '../services/queriesService'
import { transformParQData } from '../utils/contentfulTransformers'

/**
 * Hook para obtener los datos de ParQ de Contentful
 * Maneja contenido, preguntas y modales
 *
 * @returns {Object} Objeto con loading, error y todos los datos de ParQ
 */
export const useContentfulParQ = () => {
	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'parq'] as const,
		queryFn: queriesApi.getParQ,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Manejar errores
	if (error) {
		console.error('Error al obtener datos de ParQ desde Contentful:', error)
	}

	// Procesar datos usando memoización
	const processedData = useMemo(() => {
		if (!rawData || isLoading || error) {
			return {
				contParQ: false,
				parQQuestions: false,
				parQModal: false
			}
		}

		try {
			return transformParQData(rawData)
		} catch (transformError) {
			console.error('Error al transformar datos de ParQ:', transformError)
			return {
				contParQ: false,
				parQQuestions: false,
				parQModal: false
			}
		}
	}, [rawData, isLoading, error])

	// Información del cache
	const cacheInfo = useMemo(
		() => ({
			isStale,
			isRefetching,
			lastFetch: rawData ? new Date() : null,
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
		}),
		[isStale, isRefetching, rawData]
	)

	return {
		// Datos principales (compatibilidad con hook original)
		loading: isLoading,
		error,
		...processedData, // contParQ, parQQuestions, parQModal

		// Funcionalidades adicionales
		refetch,
		isStale,
		isRefetching,
		cacheInfo,
		rawData
	}
}

export default useContentfulParQ
